<script lang="ts">
	import { page } from '$app/state';
	import { DropdownSelect } from '$lib/shared/ui';
	import type { SelectOption } from '$lib/shared/lib/popup-types';
	import type { User } from '$lib/shared/lib/types';
	import type { Page } from '@sveltejs/kit';
	import { onMount } from 'svelte';

	interface Props {
		user: User | null;
		showAvatars?: boolean;
	}

	let { user, showAvatars = true }: Props = $props();

	let dropdownButton = $state<HTMLElement | undefined>();

	// Local minimal shape; matches the columns we read from each stream entry
	// (the broader app `Stream` uses `icon` for the avatar field).
	type StreamRow = {
		tag: string;
		name: string;
		icon: string | null;
		[key: string]: unknown;
	};

	function getStreamOptions(user: User | null, page: Page): SelectOption[] {
		if (!user?.streams) return [];

		let pathFromTag = (tag: string) => `/admin/${tag}/panel`;

		if (page.params?.tag && page.route.id) {
			const pageElements = page.route.id.split('/');
			const urlElements = page.url.pathname.split('/');
			// override lambda to replace where tag appears in url based on how route is interpreted
			// this means that [tag] could be overridden multiple times or if "tag" is part of the route
			// but not a param it will not be overridden
			pathFromTag = (tag: string) => {
				for (let i = 0; i < pageElements.length; i++) {
					if (pageElements[i] == '[tag]') {
						urlElements[i] = tag;
					}
				}
				return urlElements.join('/');
			};
		}

		return (user.streams as unknown as StreamRow[])
			.sort((a, b) => a.name.localeCompare(b.name))
			.map((stream) => ({
				value: stream.tag,
				label: stream.name,
				href: pathFromTag(stream.tag),
				avatar: stream.icon || undefined
			}));
	}

	onMount(() => {
		const handleKeydown = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
				e.preventDefault();
				const button = dropdownButton?.querySelector('button');
				if (button) {
					button.click();
				}
			}
		};

		window.addEventListener('keydown', handleKeydown);

		return () => {
			window.removeEventListener('keydown', handleKeydown);
		};
	});
	let tag = $derived(page.params.tag);
	// Convert streams to SelectOption format
	let streamOptions = $derived(getStreamOptions(user, page));
</script>

{#if user && (user.streams.length > 1 || user.superuser)}
	<div class="flex justify-center">
		<div class="max-w-[10rem] lg:max-w-full" bind:this={dropdownButton}>
			<DropdownSelect
				options={streamOptions}
				selected={tag || ''}
				placeholder="Select a stream"
				buttonClass="btn w-full justify-between"
				searchable={true}
				maxHeight="max-h-64"
				{showAvatars}
			/>
		</div>
	</div>
{/if}
